import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Zap, Search, Wand2, Sunrise } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const difficultyDot = (d) => {
    if (d === "easy") return "bg-sage";
    if (d === "hard") return "bg-terracotta";
    return "bg-amber-500";
};

export default function Home() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [prompts, setPrompts] = useState([]);
    const [daily, setDaily] = useState(null);
    const [activeCat, setActiveCat] = useState("All");
    const [search, setSearch] = useState("");
    const [customText, setCustomText] = useState("");

    useEffect(() => {
        Promise.all([api.get("/prompts"), api.get("/prompts/daily")])
            .then(([all, dly]) => {
                setPrompts(all.data);
                setDaily(dly.data);
            })
            .catch(() => toast.error("Couldn't load prompts"));
    }, []);

    const categories = useMemo(() => ["All", ...Array.from(new Set(prompts.map((p) => p.category)))], [prompts]);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return prompts.filter((p) => {
            if (activeCat !== "All" && p.category !== activeCat) return false;
            if (s && !(`${p.title} ${p.text}`.toLowerCase().includes(s))) return false;
            return true;
        });
    }, [prompts, activeCat, search]);

    const startCustom = () => {
        if (customText.trim().length < 6) {
            toast.error("Tell us what to speak about (at least a few words)");
            return;
        }
        const params = new URLSearchParams({ text: customText.trim().slice(0, 800), title: "Custom prompt" });
        navigate(`/practice/custom?${params.toString()}`);
    };

    return (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12" data-testid="home-page">
            {/* Hero */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400 flex items-center gap-2">
                    <Sparkles size={12} strokeWidth={1.5} />
                    <span>Hi {user?.name?.split(" ")[0] || "there"} — ready for a rep?</span>
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light mt-3 leading-[1.05]">
                    Speak it. <em className="italic text-terracotta">Sharpen it.</em>
                </h1>
                <p className="text-base text-stone-600 dark:text-stone-400 mt-3 max-w-xl leading-relaxed">
                    Pick a prompt, answer out loud, and your AI coach scores your clarity, structure, content, and delivery — with the exact line to fix next time.
                </p>
            </motion.div>

            {/* Daily prompt of the day */}
            {daily && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 rounded-3xl border border-terracotta/30 bg-gradient-to-br from-terracotta/[0.08] to-sage/[0.06] dark:from-terracotta/15 dark:to-sage/10 p-6 sm:p-7 relative overflow-hidden"
                    data-testid="daily-prompt-card"
                >
                    <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-terracotta/10 blur-2xl pointer-events-none" />
                    <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-bold text-terracotta">
                        <Sunrise size={12} strokeWidth={1.5} />
                        Today's prompt
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-medium mt-2 leading-tight">
                        {daily.title}
                    </h2>
                    <p className="text-sm sm:text-base text-stone-700 dark:text-stone-300 mt-2 leading-relaxed italic font-serif">
                        “{daily.text}”
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                        <Link
                            to={`/practice/${daily.id}`}
                            data-testid="daily-prompt-start"
                            className="inline-flex items-center gap-2 rounded-full px-5 h-11 bg-terracotta hover:bg-[#D05A40] text-white font-medium text-sm"
                        >
                            Take it on <ArrowRight size={16} />
                        </Link>
                        <span className="text-xs text-stone-500">{daily.category} · ~{daily.target_seconds}s</span>
                    </div>
                </motion.div>
            )}

            {/* Custom prompt */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-6"
                data-testid="custom-prompt-card"
            >
                <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">
                    <Wand2 size={12} strokeWidth={1.5} /> Bring your own prompt
                </div>
                <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-3">
                    <Textarea
                        data-testid="custom-prompt-input"
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        placeholder="e.g. Pitch our new product to an investor in 60 seconds…"
                        className="rounded-2xl resize-none min-h-[68px]"
                    />
                    <Button
                        onClick={startCustom}
                        data-testid="custom-prompt-start"
                        className="rounded-full px-5 h-[68px] sm:h-auto sm:self-stretch bg-stone-900 dark:bg-white dark:text-stone-900 text-white hover:bg-stone-800 dark:hover:bg-stone-100"
                    >
                        Start <ArrowRight size={16} className="ml-2" />
                    </Button>
                </div>
            </motion.div>

            {/* Library */}
            <div className="mt-12">
                <div className="flex items-end justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400 flex items-center gap-2">
                            <Zap size={12} strokeWidth={1.5} /> Prompt library
                        </div>
                        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-medium mt-1">Pick your rep</h2>
                    </div>
                    <div className="relative">
                        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search prompts…"
                            className="pl-10 rounded-full w-64 h-10"
                            data-testid="prompt-search-input"
                        />
                    </div>
                </div>

                <div className="mt-5 flex gap-2 flex-wrap">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setActiveCat(cat)}
                            data-testid={`prompt-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-4 py-1.5 rounded-full text-xs tracking-wider uppercase font-medium border transition-colors
                                ${activeCat === cat
                                    ? "bg-stone-900 dark:bg-white text-white dark:text-stone-900 border-transparent"
                                    : "bg-transparent text-stone-600 dark:text-stone-400 border-stone-200 dark:border-stone-700 hover:border-stone-400"}`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    {filtered.map((p, i) => (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <Link
                                to={`/practice/${p.id}`}
                                data-testid={`prompt-card-${p.id}`}
                                className="group block rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-6 h-full hover:shadow-[0_8px_30px_rgba(44,43,42,0.08)] transition-all"
                            >
                                <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">
                                    <span>{p.category}</span>
                                    <span className="flex items-center gap-1.5">
                                        <span className={`w-1.5 h-1.5 rounded-full ${difficultyDot(p.difficulty)}`} />
                                        {p.difficulty}
                                    </span>
                                </div>
                                <h3 className="font-serif text-2xl sm:text-3xl font-medium tracking-tight mt-2 leading-tight">
                                    {p.title}
                                </h3>
                                <p className="text-sm text-stone-600 dark:text-stone-400 mt-2 line-clamp-2 leading-relaxed">
                                    {p.text}
                                </p>
                                <div className="mt-4 flex items-center justify-between">
                                    <span className="text-xs text-stone-400">~{p.target_seconds}s target</span>
                                    <span className="text-xs text-terracotta opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                        Start <ArrowRight size={12} />
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
                {filtered.length === 0 && (
                    <div className="text-center text-stone-500 py-12 italic font-serif text-xl">No prompts match.</div>
                )}
            </div>
        </div>
    );
}
